import { Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import { DeactivationCategoriesService, DeactivationCategory } from 'src/app/services/deactivation-categories/deactivation-categories.service';
import { AuthService } from 'src/app/services/auth/auth.service';

// Mismos 8 motivos hardcodeados que en contact-profile.component.ts (línea ~129).
// Mantener en sync si se agrega/quita un motivo allá.
const REASONS: { index: number; label: string }[] = [
  { index: 1, label: 'Morosidad (Permite activarse al cancelar la deuda si aplica)' },
  { index: 2, label: 'Fallecimiento' },
  { index: 3, label: 'Fraude' },
  { index: 4, label: 'Falsificación de documentos' },
  { index: 5, label: 'Perdida incobrable' },
  { index: 6, label: 'Solicitado por el cliente' },
  { index: 7, label: 'Ex-empleado' },
  { index: 8, label: 'Incumplimiento de pago' }
];

@Component({
  selector: 'app-deactivation-categories',
  templateUrl: './deactivation-categories.component.html'
})
export class DeactivationCategoriesComponent implements OnInit {
  reasons = REASONS;
  categories: DeactivationCategory[] = [];
  loading = false;

  showModal = false;
  isEditing = false;
  currentId: string | null = null;

  form: any = {
    code: '',
    name: '',
    message_for_client: '',
    option_indexes: [] as number[],
    automatic_keywords_str: '',
    order: 999,
    status: true
  };
  formErrors: { code?: string; name?: string; message_for_client?: string } = {};
  submitting = false;

  // Mapa de qué motivos/keywords ya están tomados por OTRAS categorías.
  // Se refresca al abrir el modal (excluyendo la categoría actual si se edita).
  takenOptions: { [index: number]: { category_code: string; category_name: string } } = {};
  takenKeywords: { [kw: string]: { category_code: string; category_name: string } } = {};

  constructor(
    private svc: DeactivationCategoriesService,
    public _auth: AuthService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load = async () => {
    this.loading = true;
    try {
      const r = await this.svc.list();
      this.categories = (r?.items || []).sort((a, b) => (a.order || 999) - (b.order || 999));
    } catch (e: any) {
      Swal.fire('Error', e?.error?.error || 'No se pudo cargar el catálogo', 'error');
      this.categories = [];
    } finally {
      this.loading = false;
    }
  };

  reasonLabelFor(index: number): string {
    const r = this.reasons.find(x => x.index === index);
    return r ? r.label : `#${index}`;
  }

  openCreate = async () => {
    this.isEditing = false;
    this.currentId = null;
    this.resetForm();
    await this.loadAssignmentMap(null);
    this.showModal = true;
  };

  openEdit = async (c: DeactivationCategory) => {
    this.isEditing = true;
    this.currentId = c._id || null;
    this.form = {
      code: c.code,
      name: c.name,
      message_for_client: c.message_for_client,
      option_indexes: [...(c.option_indexes || [])],
      automatic_keywords_str: (c.automatic_keywords || []).join(', '),
      order: c.order ?? 999,
      status: c.status !== false
    };
    this.formErrors = {};
    await this.loadAssignmentMap(c._id || null);
    this.showModal = true;
  };

  loadAssignmentMap = async (excludeId: string | null) => {
    try {
      const r = await this.svc.getAssignmentMap(excludeId);
      this.takenOptions = r?.options || {};
      this.takenKeywords = r?.keywords || {};
    } catch (e) {
      this.takenOptions = {};
      this.takenKeywords = {};
    }
  };

  isOptionTaken = (index: number): boolean => {
    return !!this.takenOptions[index];
  };

  takenByCategoryName = (index: number): string | null => {
    return this.takenOptions[index]?.category_name || null;
  };

  closeModal = () => {
    this.showModal = false;
    this.isEditing = false;
    this.currentId = null;
    this.resetForm();
  };

  resetForm = () => {
    this.form = {
      code: '',
      name: '',
      message_for_client: '',
      option_indexes: [],
      automatic_keywords_str: '',
      order: 999,
      status: true
    };
    this.formErrors = {};
  };

  toggleOption = (index: number, checked: boolean) => {
    // Si está tomado por otra categoría, no permitir marcarlo (defensa UI).
    if (checked && this.isOptionTaken(index)) return;
    const set = new Set<number>(this.form.option_indexes);
    if (checked) set.add(index); else set.delete(index);
    this.form.option_indexes = Array.from(set).sort((a, b) => a - b);
  };

  isOptionChecked = (index: number): boolean => {
    return (this.form.option_indexes || []).includes(index);
  };

  validate = (): boolean => {
    const errs: any = {};
    if (!this.form.code || !/^[A-Z0-9_]+$/.test(this.form.code)) {
      errs.code = 'code es obligatorio (solo mayúsculas, números y guion bajo)';
    }
    if (!this.form.name || this.form.name.trim().length < 2) {
      errs.name = 'name es obligatorio';
    }
    if (!this.form.message_for_client || this.form.message_for_client.trim().length < 5) {
      errs.message_for_client = 'message_for_client es obligatorio';
    }
    this.formErrors = errs;
    return Object.keys(errs).length === 0;
  };

  save = async () => {
    if (!this.validate()) return;
    this.submitting = true;

    const keywords = String(this.form.automatic_keywords_str || '')
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean);

    const payload: Partial<DeactivationCategory> = {
      code: this.form.code.toUpperCase(),
      name: this.form.name.trim(),
      message_for_client: this.form.message_for_client.trim(),
      option_indexes: this.form.option_indexes,
      automatic_keywords: keywords,
      order: Number(this.form.order) || 999,
      status: this.form.status !== false
    };

    try {
      if (this.isEditing && this.currentId) {
        const r = await this.svc.update(this.currentId, payload);
        if (!r?.ok) throw new Error(r?.error || 'No se pudo actualizar');
        Swal.fire('Actualizado', '', 'success');
      } else {
        const r = await this.svc.create(payload);
        if (!r?.ok) throw new Error(r?.error || 'No se pudo crear');
        Swal.fire('Creado', '', 'success');
      }
      this.closeModal();
      await this.load();
    } catch (e: any) {
      // Si el backend reportó conflicto de asignación, refrescamos el mapa para que
      // el form lo refleje sin tener que cerrar/reabrir.
      if (e?.error?.conflicts) {
        await this.loadAssignmentMap(this.currentId);
      }
      Swal.fire('Error', e?.error?.error || e?.message || 'Error', 'error');
    } finally {
      this.submitting = false;
    }
  };

  remove = async (c: DeactivationCategory) => {
    const r = await Swal.fire({
      title: '¿Eliminar categoría?',
      html: `<div class="text-left"><strong>${c.code}</strong> · ${c.name}<br><span class="text-gray-500 text-sm">Los clientes asociados dejarán de mostrar el mensaje hasta que asignes los motivos a otra categoría.</span></div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed || !c._id) return;
    try {
      const resp = await this.svc.delete(c._id);
      if (!resp?.ok) throw new Error(resp?.error || 'No se pudo eliminar');
      Swal.fire('Eliminada', '', 'success');
      await this.load();
    } catch (e: any) {
      Swal.fire('Error', e?.error?.error || e?.message || 'Error', 'error');
    }
  };
}
